import { useState } from 'react';
import { BedDouble, ChevronDown, Ruler, Star, Users, type LucideIcon } from 'lucide-react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Collapsible, CollapsibleContent } from '@voll-intelligence/ui-kit/ui/collapsible';
import { OutOfPolicyMark } from '../../policy/out_of_policy_mark.js';
import type {
  HotelRatingView,
  HotelRateView,
  HotelRoomsContextView,
  HotelRoomView,
} from '../models.js';
import type { HotelRoomsCopy } from './hotel_rooms.js';
import { HotelRoomRates } from './hotel_room_rates.js';
import { RateBenefitChips, rateHasBenefitChips } from './rate_chips.js';
import { RoomPhotoCarousel } from './room_photo_carousel.js';

/**
 * O card de QUARTO — **o card de hotel do design hub com outro conteúdo**, não
 * outro card: foto à esquerda na altura do card · selos interrompendo a borda
 * depois da foto · corpo no meio · trilho de decisão à direita (preço em cima,
 * ação embaixo) · painel de tarifas expandindo dentro do card.
 *
 * Abaixo de `@xl` (576px de container) o trilho DESCE e vira faixa — é isto que
 * faz o card caber num drawer de 420px. A foto e o corpo ficam lado a lado nos
 * dois casos: a foto é a âncora da linha.
 */

/** Área, capacidade e cama numa linha — ícone + caption, mesma forma dos
 *  amenities do hotel. Exportado porque o modal de detalhe diz a mesma linha. */
export function HotelRoomFacts({ room }: { readonly room: HotelRoomView }) {
  const facts: { label: string; Icon: LucideIcon }[] = [];
  if (room.areaLabel) facts.push({ label: room.areaLabel, Icon: Ruler });
  if (room.capacityLabel) facts.push({ label: room.capacityLabel, Icon: Users });
  if (room.bedLabel) facts.push({ label: room.bedLabel, Icon: BedDouble });
  if (facts.length === 0) return null;
  return (
    <p className="text-caption text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 font-medium">
      {facts.map(({ label, Icon }) => (
        <span key={label} className="inline-flex min-w-0 items-center gap-1.5">
          <Icon className="size-3 shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
        </span>
      ))}
    </p>
  );
}

/** Nota + contagem, estrela cheia em ink: a linha tem UMA escala
 *  (`text-caption`) e a hierarquia é peso e cor. A contagem some no card
 *  apertado (`@max-md`): ela é o lastro da nota, mas é o primeiro pedaço a
 *  pagar quando a coluna encolhe. */
function HotelRoomRating({ rating }: { readonly rating: HotelRatingView | undefined }) {
  if (!rating) return null;
  const value =
    rating.valueLabel ?? (rating.value === undefined ? undefined : String(rating.value));
  return (
    <span className="text-caption text-muted-foreground flex shrink-0 items-baseline gap-1 font-medium">
      <Star
        className="size-3.5 shrink-0 translate-y-px fill-foreground text-foreground"
        aria-hidden
      />
      <span className="sr-only">{rating.label}</span>
      {value ? (
        <span aria-hidden className="text-foreground font-semibold tabular-nums">
          {value}
        </span>
      ) : null}
      {rating.reviewsLabel ? (
        <span className="hidden tabular-nums @md:inline">{rating.reviewsLabel}</span>
      ) : null}
    </span>
  );
}

/** O selo que interrompe a borda de cima: DE ONDE vem o preço — "Tarifa
 *  negociada" no lugar do nome do fornecedor Acordo (#6914). O offset é a
 *  largura da foto + os 14px de padding do corpo, e acompanha o
 *  `w-32 @2xl:w-44` do carrossel. */
function HotelRoomBadges({
  hasPhoto,
  rate,
  copy,
}: {
  readonly hasPhoto: boolean;
  readonly rate: HotelRateView | undefined;
  readonly copy: HotelRoomsCopy;
}) {
  const channelLabel = rate?.negotiated ? copy.negotiatedLabel : rate?.channelLabel;
  if (!channelLabel) return null;
  return (
    <div
      className={`absolute -top-2.5 z-10 flex gap-1.5 ${
        hasPhoto ? 'left-[8.875rem] @2xl:left-[11.875rem]' : 'left-3.5'
      }`}
    >
      <Badge variant="soft" color="neutral" className="ring-card ring-2">
        {channelLabel}
      </Badge>
    </div>
  );
}

/** "Últimos 2" — escassez da TARIFA mostrada, não do quarto: cada canal tem o
 *  próprio estoque. Acima de 5 restantes o número não decide nada. Âmbar é
 *  urgência, não erro nem economia. */
function ScarcityBadge({
  count,
  copy,
}: {
  readonly count?: number;
  readonly copy: HotelRoomsCopy;
}) {
  if (count === undefined || !Number.isInteger(count) || count <= 0 || count > 5) return null;
  return (
    <Badge variant="soft" color="warning" className="shrink-0">
      {copy.scarcityLabel(count)}
    </Badge>
  );
}

/** `Selecionado:` porque o botão diz só o verbo — sem isto a pessoa confirma
 *  sem saber qual das tarifas do painel está sendo levada. O nome do plano em
 *  ink: é o dado, o rótulo é só a preposição dele. */
function SelectionLegend({
  rate,
  copy,
}: {
  readonly rate: HotelRateView;
  readonly copy: HotelRoomsCopy;
}) {
  if (rate.planLabel) {
    return (
      <span className="font-semibold">
        {copy.selectedNotePrefix} <span className="text-foreground">{rate.planLabel}</span>
      </span>
    );
  }
  const channel = rate.negotiated ? copy.negotiatedLabel : rate.channelLabel;
  return channel ? <>{channel}</> : null;
}

/** O mesmo texto do `SelectionLegend`, em string — para o `title` nativo do
 *  span que trunca. Fora do `DecisionRail` só pelo teto de complexidade do
 *  ESLint: o ternário aqui some do JSX do trilho. */
function selectionLegendTitle(rate: HotelRateView, copy: HotelRoomsCopy): string | undefined {
  if (rate.planLabel) return `${copy.selectedNotePrefix} ${rate.planLabel}`;
  return rate.negotiated ? copy.negotiatedLabel : rate.channelLabel;
}

/** Quando a ação do trilho conclui, quem abre o painel é este link — mesma
 *  forma do `Ver detalhes` (sublinhado + ink), com o caret que o botão sólido
 *  não tem. */
function ExpandLink({
  label,
  open,
  onToggle,
}: {
  readonly label: string;
  readonly open: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="text-ui hover:text-foreground text-muted-foreground flex w-full cursor-pointer items-center justify-center gap-1 py-1 transition-colors"
    >
      <span className="underline underline-offset-3">{label}</span>
      <ChevronDown
        className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        aria-hidden
      />
    </button>
  );
}

/**
 * O trilho de decisão do card: preço no topo (diária + unidade + total da
 * estadia — hotel tem DUAS moedas), ação sólida no pé. Empilhado (abaixo de
 * `@xl`) o trilho vira LINHA: preço à esquerda, ação à direita — no drawer,
 * altura é o recurso caro. A legenda só existe a partir de `@2xl`: é a primeira
 * linha a pagar quando aperta.
 */
function DecisionRail({
  rate,
  copy,
  selected,
  frozen,
  disabled,
  comparison,
  open,
  onToggle,
  onSelect,
}: {
  readonly rate: HotelRateView;
  readonly copy: HotelRoomsCopy;
  /** Esta é A tarifa marcada (recibo, seleção confirmada, OU só a sugestão do
   *  agente) — só decide o atributo de dados do total (`hotel-room-total`),
   *  não trava o botão sozinha. Ver `frozen`. */
  readonly selected: boolean;
  /** Trava o CTA em "Selecionado" — recibo ou seleção já CONFIRMADA pela
   *  pessoa. Separado de `selected` de propósito: a sugestão do agente (Fase
   *  1.7, `selectionConfirmed=false` em `HotelRoomGroup`) marca a tarifa sem
   *  travar — a pessoa ainda precisa tocar pra escolher. */
  readonly frozen: boolean;
  readonly disabled: boolean;
  readonly comparison: boolean;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onSelect: () => void;
}) {
  /* `nightlyLabel` é o formato legado (preço e unidade numa string só) — vale
     como preço da linha, mas nunca ganha a unidade separada. */
  const nightly = rate.nightlyPriceLabel ?? rate.nightlyLabel;
  const totalLine = rate.stayTotalLabel ?? (nightly ? rate.totalLabel : '');
  const legendTitle = selectionLegendTitle(rate, copy);
  return (
    <div className="border-border flex w-full items-center justify-between gap-3 border-t p-3.5 @xl:grid @xl:w-44 @xl:grid-cols-1 @xl:content-between @xl:border-t-0 @xl:border-l">
      <div className="grid gap-0.5">
        {/* Preço sem gap da unidade: eles são um dado só, e a barra que a
            unidade traz já é o separador. */}
        <span className="flex items-baseline">
          <span className="text-heading shrink-0 tabular-nums">{nightly ?? rate.totalLabel}</span>
          {rate.nightlyPriceLabel && rate.nightlyUnitLabel ? (
            <span className="text-caption text-muted-foreground min-w-0 truncate">
              {rate.nightlyUnitLabel}
            </span>
          ) : null}
        </span>
        {/* O total da estadia em ink: é dinheiro que a pessoa vai pagar, não
            anotação sobre o número de cima. */}
        {totalLine ? (
          <span
            className="text-caption text-foreground font-medium"
            {...(selected && rate.total
              ? {
                  'data-money-role': 'hotel-room-total',
                  'data-money-amount': String(rate.total.amount),
                  'data-money-currency': rate.total.currency,
                }
              : {})}
          >
            {totalLine}
          </span>
        ) : null}
      </div>

      {/* No pé há até TRÊS objetos (legenda, botão, link) — `gap-1` mantém eles
          como um bloco só. */}
      <div className="grid shrink-0 gap-1">
        <span
          {...(legendTitle ? { title: legendTitle } : {})}
          className="text-caption text-muted-foreground hidden min-w-0 truncate font-medium @2xl:block"
        >
          <SelectionLegend rate={rate} copy={copy} />
        </span>
        {frozen ? (
          <Button className="w-full" disabled>
            {copy.selectedRateLabel}
          </Button>
        ) : (
          <Button className="w-full" disabled={disabled || !rate.available} onClick={onSelect}>
            {copy.selectRateLabel}
          </Button>
        )}
        {comparison ? (
          <ExpandLink label={copy.showBenefitsLabel} open={open} onToggle={onToggle} />
        ) : null}
      </div>
    </div>
  );
}

/** A tarifa que o card mostra fechado: a marcada (preview/seleção), senão a
 *  primeira — no recibo, só a selecionada vale. */
function displayedRoomRate(
  visibleRates: readonly HotelRateView[],
  receipt: boolean,
  wantedId: string | undefined,
) {
  return (
    visibleRates.find((rate) => rate.id === wantedId) ?? (receipt ? undefined : visibleRates[0])
  );
}

/** Trava o CTA em "Selecionado": recibo, ou a tarifa marcada (`selected`) com
 *  a seleção já CONFIRMADA. A sugestão do agente (Fase 1.7) chega com
 *  `selectionConfirmed=false` e marca `selected` sem travar. Fora de
 *  `roomCardState` pelo mesmo teto de complexidade: são 3 operadores que ela
 *  não tinha orçamento pra pagar. */
function frozenSelection(
  receipt: boolean,
  selected: boolean,
  selectionConfirmed: boolean | undefined,
): boolean {
  return receipt || (selected && (selectionConfirmed ?? true));
}

/** As derivações do card, fora do componente pelo teto de complexidade: cada
 *  `?.`/`??`/ternário conta como ramo, e o JSX do card já gasta o orçamento. */
function roomCardState(input: {
  room: HotelRoomView;
  receipt: boolean;
  selectedRateId: string | undefined;
  previewRateId: string | undefined;
  /** Ver `HotelRoomGroup.selectionConfirmed` / `frozenSelection`. */
  selectionConfirmed: boolean | undefined;
}) {
  const { room, receipt, selectedRateId, previewRateId, selectionConfirmed } = input;
  const visibleRates = receipt
    ? room.rates.filter((rate) => rate.id === selectedRateId)
    : room.rates;
  const displayedRate = displayedRoomRate(
    visibleRates,
    receipt,
    receipt ? selectedRateId : previewRateId,
  );
  const selected = room.rates.some((rate) => rate.id === selectedRateId);
  return {
    visibleRates,
    displayedRate,
    chosenRateId: displayedRate?.id ?? '',
    selected,
    frozen: frozenSelection(receipt, selected, selectionConfirmed),
    selectedComparison: visibleRates.slice(1).some((rate) => rate.id === selectedRateId),
    /* O link `Comparar tarifas` existe com QUALQUER tarifa, não só com duas —
       regra do protótipo (`onVerTarifas` é incondicional lá). Com um canal só,
       o painel ainda diz QUEM vende (fornecedor por extenso + logo + plano +
       garantia de cancelamento) — sem isto, quarto de tarifa única esconde o
       fornecedor e não há como conferir a origem do preço. */
    hasComparison: !receipt && visibleRates.length > 0,
    outOfPolicy: displayedRate?.policyTone === 'outside',
    policyReasons: displayedRate?.policyReasons,
    hasPhoto: room.photos.length > 0,
    detailsAvailable: room.photos.length > 0 || Boolean(room.description) || Boolean(room.about),
  };
}

/** A tarifa marcada no painel — só o retorno visual; quem confirma é o botão
 *  do trilho, com o id que está aqui. Segue a seleção do host e é REVALIDADA
 *  contra o recorte dos chips (#6470): a que sai das tarifas à vista volta à
 *  seleção do host (ou a nenhuma) — sem isto, ela sumia do card com o chip
 *  ligado e reaparecia sozinha quando o chip era solto. */
function usePreviewRate(rates: readonly HotelRateView[], selectedRateId: string | undefined) {
  const [previewRateId, setPreviewRateId] = useState(selectedRateId);
  // Both adjustments happen during render: a new host selection wins, and a rate
  // the chips hid goes back to the host's.
  const [followedRateId, setFollowedRateId] = useState(selectedRateId);
  if (followedRateId !== selectedRateId) {
    setFollowedRateId(selectedRateId);
    if (selectedRateId) setPreviewRateId(selectedRateId);
  }
  const previewShown =
    previewRateId === undefined || rates.some((rate) => rate.id === previewRateId);
  if (!previewShown && previewRateId !== selectedRateId) setPreviewRateId(selectedRateId);
  return [previewRateId, setPreviewRateId] as const;
}

export function HotelRoomGroup({
  room,
  copy,
  context,
  selectedRateId,
  selectionConfirmed,
  receipt,
  disabled,
  onRateSelect,
  onDetailsOpen,
}: {
  readonly room: HotelRoomView;
  readonly copy: HotelRoomsCopy;
  readonly context?: HotelRoomsContextView;
  readonly selectedRateId?: string;
  /** `selectedRateId` já foi CONFIRMADO (recibo, ou seleção em andamento pro
   *  checkout — o uso de sempre) ou é só a SUGESTÃO do agente, ainda por
   *  escolher (Fase 1.7: quarto citado na conversa)? `undefined`/`true`
   *  preserva o travamento de sempre; `false` revela a tarifa (painel
   *  aberto, rádio marcado) sem travar o CTA. Campo OBRIGATÓRIO (não `?:`) de
   *  propósito: com `exactOptionalPropertyTypes`, um opcional ausente aqui
   *  custaria mais um ternário no orçamento do componente — o default mora
   *  em `roomCardState`. */
  readonly selectionConfirmed: boolean | undefined;
  readonly receipt: boolean;
  readonly disabled: boolean;
  readonly onRateSelect: (roomId: string, rateId: string) => void;
  readonly onDetailsOpen: (roomId: string) => void;
}) {
  const [previewRateId, setPreviewRateId] = usePreviewRate(room.rates, selectedRateId);
  const [comparisonOpen, setComparisonOpen] = useState<boolean>();

  const state = roomCardState({
    room,
    receipt,
    selectedRateId,
    previewRateId,
    selectionConfirmed,
  });
  const { displayedRate, hasPhoto, detailsAvailable, hasComparison } = state;
  const panelOpen = hasComparison && (comparisonOpen ?? state.selectedComparison);

  // `isolate`: os z-10 internos (badges, escudo de política) disputam só entre
  // si — sem isso empatam com o z-10 das abas sticky do painel de detalhes e,
  // vindo depois no DOM, pintam POR CIMA delas no scroll.
  return (
    <article
      // O anel fica; na thread o card de chat soma a elevação do bloco
      // (`--block-elevation`, #6296) — no painel de detalhes, nada.
      className="bg-card ring-foreground/10 @container relative isolate grid min-w-0 rounded-xl shadow-[var(--block-elevation,0_0_#0000)] ring-1"
      aria-current={state.selected || undefined}
      aria-label={room.name}
    >
      <HotelRoomBadges hasPhoto={hasPhoto} rate={displayedRate} copy={copy} />

      {/* O escudo no VÉRTICE: o canto superior direito é área nobre que nada
          usa, e assim o marcador fica no MESMO lugar que no card de voo. */}
      {state.outOfPolicy ? (
        <div className="absolute top-2 right-2 z-10">
          <OutOfPolicyMark
            label={copy.outOfPolicyLabel}
            reasons={state.policyReasons}
            hint={copy.outOfPolicyHint}
          />
        </div>
      ) : null}

      <div className="@xl:flex @xl:items-stretch grid min-w-0">
        <div className="flex min-w-0 flex-1 items-stretch">
          {hasPhoto ? (
            <RoomPhotoCarousel photos={room.photos} copy={copy} open={panelOpen} />
          ) : null}

          {/* `content-between`: os fatos no topo, o `Ver detalhes` ancorado no
              piso — que é o mesmo piso do botão do trilho. */}
          <div className="grid min-w-0 flex-1 content-between gap-2 p-3.5 pt-4">
            <div className="grid min-w-0 gap-2">
              {/* O nome tem a linha inteira: com a nota do lado ele truncava no
                  apertado, e o nome é o que identifica a opção. */}
              <span className="flex min-w-0 items-center gap-1.5">
                <h3 className="text-title min-w-0 truncate">{room.name}</h3>
                <ScarcityBadge count={displayedRate?.remainingRooms} copy={copy} />
              </span>
              <HotelRoomFacts room={room} />
            </div>

            {/* O que a tarifa pré-selecionada entrega, repetido do painel de
                propósito: fechado, o card promete a ação sem dizer o que está
                sendo levado. */}
            {displayedRate && rateHasBenefitChips(displayedRate) ? (
              <span className="text-caption flex min-w-0 flex-wrap items-center gap-1">
                <RateBenefitChips rate={displayedRate} copy={copy} />
              </span>
            ) : null}

            {/* A linha de horizonte do corpo: o link à esquerda (é sobre o
                QUARTO, não sobre o preço), a nota do hotel à direita. */}
            <div className="flex min-w-0 items-center justify-between gap-3">
              {detailsAvailable ? (
                <button
                  type="button"
                  onClick={() => {
                    onDetailsOpen(room.id);
                  }}
                  className="text-ui text-muted-foreground hover:text-foreground cursor-pointer underline underline-offset-3 transition-colors"
                >
                  {copy.roomDetailsLabel}
                </button>
              ) : (
                <span />
              )}
              <HotelRoomRating rating={context?.rating} />
            </div>
          </div>
        </div>

        {displayedRate ? (
          <DecisionRail
            rate={displayedRate}
            copy={copy}
            selected={state.selected}
            frozen={state.frozen}
            disabled={disabled}
            comparison={hasComparison}
            open={panelOpen}
            onToggle={() => {
              setComparisonOpen((open) => !(open ?? state.selectedComparison));
            }}
            onSelect={() => {
              onRateSelect(room.id, displayedRate.id);
            }}
          />
        ) : (
          <p className="border-border text-caption text-muted-foreground border-t p-3.5 @xl:border-t-0 @xl:border-l">
            {copy.noRatesLabel}
          </p>
        )}
      </div>

      {hasComparison ? (
        <Collapsible open={panelOpen}>
          <CollapsibleContent className="panel-collapse">
            <HotelRoomRates
              rates={state.visibleRates}
              copy={copy}
              chosenRateId={state.chosenRateId}
              disabled={disabled}
              onRatePreview={setPreviewRateId}
            />
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </article>
  );
}
